import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { useMsal, useIsAuthenticated as useMsalIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { msalInstance, loginRequest } from '@/config/msalConfig';
import { api } from '@/services/api';
import { canManage, isAdmin as isAdminRole } from '@/lib/permissions';

interface UserInfo {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  role: string;
  departmentId?: string;
  departmentName?: string;
  isActive: boolean;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: UserInfo | null;
  authProvider: 'entra' | 'local' | null;
  loginWithMicrosoft: () => Promise<void>;
  loginWithCredentials: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /**
   * Mutation permissions derived from user.role. Five flags share one rule today (Admin or
   * Manager) but are named per area so call sites never change if the rules diverge.
   * The API enforces the same rule; these only hide controls.
   */
  canManageContracts: boolean;
  canManageVendors: boolean;
  canManageLicenses: boolean;
  canManageDocuments: boolean;
  canManageCosts: boolean;
  /** Department budgets (#19): Admin and Manager */
  canManageBudgets: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { inProgress } = useMsal();
  const isMsalAuthenticated = useMsalIsAuthenticated();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [authProvider, setAuthProvider] = useState<'entra' | 'local' | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing auth on mount
  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;

    const initAuth = async () => {
      try {
        // Check MSAL first
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          const tokenResponse = await msalInstance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
          });
          if (tokenResponse.accessToken) {
            // Call entra-login to get/create local user
            const userInfo = await api.post<UserInfo>('/auth/entra-login', null, {
              headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
            });
            setUser(userInfo);
            setAuthProvider('entra');
            setIsLoading(false);
            return;
          }
        }

        // Check local JWT
        const localToken = localStorage.getItem('auth_token');
        if (localToken) {
          try {
            const userInfo = await api.get<UserInfo>('/auth/me');
            setUser(userInfo);
            setAuthProvider('local');
          } catch {
            localStorage.removeItem('auth_token');
          }
        }
      } catch {
        // No valid auth found - that's fine
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, [inProgress, isMsalAuthenticated]);

  const loginWithMicrosoft = useCallback(async () => {
    // Navigates the page to Microsoft; on return, main.tsx calls
    // handleRedirectPromise() before render and the initAuth effect above
    // exchanges the resulting MSAL account for our local user profile.
    await msalInstance.loginRedirect(loginRequest);
  }, []);

  const loginWithCredentials = useCallback(async (email: string, password: string) => {
    const response = await api.post<{ token: string; user: UserInfo }>('/auth/login', {
      email,
      password,
    });
    localStorage.setItem('auth_token', response.token);
    setUser(response.user);
    setAuthProvider('local');
  }, []);

  const logout = useCallback(async () => {
    setUser(null);
    setAuthProvider(null);
    localStorage.removeItem('auth_token');

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      await msalInstance.logoutRedirect({
        account: accounts[0],
        postLogoutRedirectUri: `${window.location.origin}/login`,
      });
    }
  }, []);

  const isAuthenticated = !!user;

  const permissions = useMemo(() => {
    const manage = canManage(user?.role);
    return {
      canManageContracts: manage,
      canManageVendors: manage,
      canManageLicenses: manage,
      canManageDocuments: manage,
      canManageCosts: manage,
      canManageBudgets: manage,
      isAdmin: isAdminRole(user?.role),
    };
  }, [user?.role]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        authProvider,
        loginWithMicrosoft,
        loginWithCredentials,
        logout,
        ...permissions,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
